import type { ReactElement } from 'react';
import { useMemo, useState } from 'react';

import { useListRecipesQuery } from '@/api/recipesApi';
import { Input } from '@/components/Input';

function formatDate(iso: string | null): string {
    return iso === null ? '—' : new Date(iso).toLocaleDateString('ru-RU');
}

export function RecipesPage(): ReactElement {
    const [searchInput, setSearchInput] = useState('');
    const [submittedSearch, setSubmittedSearch] = useState('');

    const queryArg = useMemo(
        () => ({
            ...(submittedSearch ? { search: submittedSearch } : {}),
            limit: 50,
            offset: 0
        }),
        [submittedSearch]
    );

    const { data, isLoading, isError } = useListRecipesQuery(queryArg);

    const items = data?.items ?? [];

    return (
        <div data-testid="recipes-page" className="space-y-4">
            <div className="flex items-center justify-between">
                <h1 className="text-2xl font-semibold text-gray-900">Рецепты</h1>
                {data && (
                    <span className="text-sm text-gray-500" data-testid="recipes-total">
                        Всего: {data.total}
                    </span>
                )}
            </div>

            <form
                onSubmit={(e) => {
                    e.preventDefault();
                    setSubmittedSearch(searchInput.trim());
                }}
                className="flex items-end gap-3 rounded border border-gray-200 bg-white p-3"
                data-testid="recipes-filters"
            >
                <div className="flex-1">
                    <Input
                        label="Поиск"
                        value={searchInput}
                        onChange={(e) => setSearchInput(e.target.value)}
                        placeholder="Название рецепта"
                        data-testid="recipes-search-input"
                    />
                </div>
                <button
                    type="submit"
                    data-testid="recipes-search-submit"
                    className="rounded bg-gray-900 px-4 py-2 text-sm font-medium text-white transition hover:bg-gray-800"
                >
                    Найти
                </button>
            </form>

            {isLoading && <div className="text-sm text-gray-500">Загрузка...</div>}
            {isError && (
                <div className="text-sm text-red-600" data-testid="recipes-error">
                    Не удалось загрузить рецепты
                </div>
            )}

            {!isLoading && !isError && (
                <div
                    className="overflow-hidden rounded border border-gray-200 bg-white"
                    data-testid="recipes-table"
                >
                    <div className="grid grid-cols-[80px_minmax(0,2fr)_minmax(0,1fr)_120px_120px_80px_80px] gap-2 border-b border-gray-200 bg-gray-50 px-3 py-2 text-xs font-medium uppercase text-gray-600">
                        <span>ID</span>
                        <span>Название</span>
                        <span>Автор</span>
                        <span>Сложность</span>
                        <span>Опубликован</span>
                        <span className="text-right">Просмотры</span>
                        <span className="text-right">Лайки</span>
                    </div>

                    {items.length === 0 && (
                        <div className="px-3 py-6 text-center text-sm text-gray-500">
                            Рецепты не найдены
                        </div>
                    )}

                    {items.map((recipe) => (
                        <div
                            key={recipe.id}
                            className="grid grid-cols-[80px_minmax(0,2fr)_minmax(0,1fr)_120px_120px_80px_80px] items-center gap-2 border-b border-gray-100 px-3 py-2 text-sm last:border-b-0"
                            data-testid={`recipe-row-${recipe.id}`}
                        >
                            <span className="truncate font-mono text-xs text-gray-500">
                                {recipe.id.slice(0, 8)}
                            </span>
                            <span className="truncate text-gray-900">{recipe.title}</span>
                            <span className="truncate text-gray-700">
                                {recipe.authorDisplayName}
                            </span>
                            <span className="text-gray-700">{recipe.difficulty}</span>
                            <span className="text-xs text-gray-600">
                                {formatDate(recipe.publishedAt)}
                            </span>
                            <span className="text-right text-gray-700">{recipe.viewsCount}</span>
                            <span className="text-right text-gray-700">{recipe.likesCount}</span>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
